import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4001/api/v2';

export interface PublicSessionVisitor {
  firstName: string;
  phone?:    string;
  email?:    string;
  capturedAt?: string;
}

export interface PublicSessionMessage {
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export interface PublicSession {
  sessionId: string;
  intent?:   'comprar' | 'alquilar' | 'tasar' | 'prestamo' | 'indeciso';
  visitor:   PublicSessionVisitor | null;
  messages:  PublicSessionMessage[];
}

export interface StartSessionInput {
  intent?: PublicSession['intent'];
  zone?:   string;
}

export interface CaptureContactInput {
  firstName: string;
  phone?:    string;
  email?:    string;
}

/**
 * API para Gustavo cliente (visitante anónimo). Usa cookie httpOnly
 * `gustavo_session` (set por el backend), por eso `credentials: 'include'`.
 *
 * El streaming de mensajes se hace fuera de RTK Query (con fetch + ReadableStream)
 * en `usePublicGustavoChat` — RTK Query no soporta SSE nativamente.
 */
export const publicGustavoApi = createApi({
  reducerPath: 'publicGustavoApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_URL}/public/gustavo`,
    credentials: 'include',
  }),
  tagTypes: ['PublicSession'],
  endpoints: (build) => ({
    startSession: build.mutation<PublicSession, StartSessionInput>({
      query: (body) => ({ url: '/sessions', method: 'POST', body }),
      transformResponse: (res: { data: PublicSession }) => res.data,
      invalidatesTags: ['PublicSession'],
    }),
    getSession: build.query<PublicSession, void>({
      query: () => ({ url: '/sessions/me' }),
      transformResponse: (res: { data: PublicSession }) => res.data,
      providesTags: ['PublicSession'],
    }),
    captureContact: build.mutation<{ visitor: PublicSessionVisitor }, CaptureContactInput>({
      query: (body) => ({ url: '/sessions/me/contact', method: 'POST', body }),
      transformResponse: (res: { data: { visitor: PublicSessionVisitor } }) => res.data,
      invalidatesTags: ['PublicSession'],
    }),
  }),
});

export const {
  useStartSessionMutation,
  useGetSessionQuery,
  useLazyGetSessionQuery,
  useCaptureContactMutation,
} = publicGustavoApi;

export const PUBLIC_GUSTAVO_BASE_URL = `${BASE_URL}/public/gustavo`;
